// import hooks
import { useAllPerformers } from "hooks/use-all-performers-data";
import { getUniqueObjects } from "helpers/data/get-unique-objects";
import { PerformerFilter } from "types/filters";

export const getPerformersForFilters = (
    filters: PerformerFilter[],
    signatureEventId?: string
): Queries.PerformerFieldsFragment[] => {

    let filteredPerformers = useAllPerformers();

    // if no filters and no signature event, return all
    if (filters.length === 0 && !signatureEventId) {
        return filteredPerformers;
    }

    // check for signatureEventId
    if (signatureEventId) {
        filteredPerformers = useAllPerformers().filter((performer) =>
            performer.performances?.some(performance => performance?.signatureEvent?.id === signatureEventId)
        );
    }

    // if there are no filters, return all performers for signature event
    if (filters.length === 0) {
        return filteredPerformers;
    }

    const performersForUniversalTags = getPerformersForUniveralTags(filteredPerformers, filters);
    const performersForPerformanceTypes = getPerformersForPerformanceTypes(filteredPerformers, filters);
    const performersForMusicGenres = getPerformersForMusicGenres(filteredPerformers, filters);

    const commonPerformers = getCommonPeformers(
        performersForUniversalTags,
        performersForPerformanceTypes,
        performersForMusicGenres
    );
    
    return commonPerformers;
};

const getPerformersForUniveralTags = (
    performers: Queries.PerformerFieldsFragment[],
    filters: PerformerFilter[]
): Queries.PerformerFieldsFragment[] => {
    
    // get filters where __typename = DatoCmsUniversalTag
    const universalTagFilters = filters.filter(
        (filter) => filter.__typename === "DatoCmsUniversalTag"
    );

    if (universalTagFilters.length === 0) {
        return [];
    }

    return performers.filter((performer) => {
        return performer.universalTags?.some((tag) => {
            return universalTagFilters.some((filter) => filter.id === tag?.id);
        });
    });
};

const getPerformersForPerformanceTypes = (
    performers: Queries.PerformerFieldsFragment[],
    filters: PerformerFilter[]
): Queries.PerformerFieldsFragment[] => {
    
    // get filters where __typename = DatoCmsPerformanceType
    const performanceTypeFilters = filters.filter(
        (filter) => filter.__typename === "DatoCmsPerformanceType"
    );

    if (performanceTypeFilters.length === 0) {
        return [];
    }

    return performers.filter((performer) => {
        return performanceTypeFilters.some(
            (filter) => filter.id === performer?.performanceType?.id
        );
    });
};

const getPerformersForMusicGenres = (
    performers: Queries.PerformerFieldsFragment[],
    filters: PerformerFilter[]
): Queries.PerformerFieldsFragment[] => {
    
    // get filters where __typename = DatoCmsMusicGenre
    const musicGenresFilters = filters.filter(
        (filter) => filter.__typename === "DatoCmsMusicGenre"
    );

    if (musicGenresFilters.length === 0) {
        return [];
    }

    return performers.filter((performer) => {
        return performer.musicGenres?.some((musicGenre) => {
            return musicGenresFilters.some(
                (filter) => filter.id === musicGenre?.id
            );
        });
    });
};

function getCommonPeformers(
    ...performerArrays: Queries.PerformerFieldsFragment[][]
): Queries.PerformerFieldsFragment[] {
    // Filter out empty arrays
    const nonEmptyArrays = performerArrays.filter(
        (array) => array.length > 0
    );
    
    if (nonEmptyArrays.length === 0) return [];

    // Extract ids from each array
    const idSets = nonEmptyArrays.map(
        (array) => new Set(array.map((performer) => performer.id))
    );

    // Find common ids
    const commonIds = [...idSets[0]].filter((id) =>
        idSets.every((set) => set.has(id))
    );

    // Filter performers to only include those with common ids
    const commonPerformers = nonEmptyArrays[0].filter((performer) =>
        commonIds.includes(performer.id)
    );

    // Remove any duplicate performers which existed in multiple input arrays
    return getUniqueObjects(commonPerformers);
}